import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  listGeneralFeedback,
  getFeedbackAnalytics,
  toggleFeedbackHighlight
} from "../../apis/supportService";
import { RefreshCw, Trash2, Star } from "lucide-react";
import toast from "react-hot-toast";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const categoryLabels = {
  UI: "UI",
  PERFORMANCE: "Performance",
  FEATURE: "Feature",
  OTHER: "Other",
  BILLING: "Billing",
  SUPPORT: "Support",
};



const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const SupportFeedbackAdmin = ({ chartRows, lastUpdated }) => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [personModal, setPersonModal] = useState(null);
  const [contentModal, setContentModal] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0, limit: 20 });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);

  const [searchVal, setSearchVal] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchVal || "");
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchVal]);

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const [feedbackResponse, analyticsResponse] = await Promise.all([
        listGeneralFeedback({
          page,
          limit,
          sortBy: "createdAt",
          sortOrder: "DESC",
          ...(debouncedSearchTerm && debouncedSearchTerm.trim() ? { search: debouncedSearchTerm.trim() } : {})
        }),
        getFeedbackAnalytics(),
      ]);
      setFeedbackItems(feedbackResponse?.items || []);
      setAnalytics(analyticsResponse || null);
      if (feedbackResponse?.pagination) setPageInfo(feedbackResponse.pagination);
    } catch (error) {
      console.error("Failed to load feedback", error);
      toast.error("Unable to load ratings data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [page, limit, debouncedSearchTerm, lastUpdated]);





  const onToggleHighlight = async (id) => {
    try {
      await toggleFeedbackHighlight(id);
      toast.success("Highlighted feedback will be reflected in the website testimonial section.");
      await loadFeedback();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to update highlight status");
    }
  };

  const averageRating = useMemo(() => {
    if (!analytics?.sentimentDistribution || analytics.sentimentDistribution.length === 0) return 0;
    let totalScore = 0;
    let totalCount = 0;
    analytics.sentimentDistribution.forEach((entry) => {
      const score = Number(entry.score || 0);
      const count = Number(entry.count || 0);
      totalScore += score * count;
      totalCount += count;
    });
    return totalCount > 0 ? Number((totalScore / totalCount).toFixed(2)) : 0;
  }, [analytics]);

  const categoryData = useMemo(() => {
    if (!analytics?.categoryBreakdown) return [];
    return analytics.categoryBreakdown.map((entry) => ({
      category: categoryLabels[entry.category] || entry.category,
      count: Number(entry.count || 0),
    }));
  }, [analytics]);

  const ratingData = useMemo(() => {
    if (!analytics?.sentimentDistribution) return [];
    return analytics.sentimentDistribution
      .map((entry) => ({
        score: Number(entry.score || 0),
        count: Number(entry.count || 0),
      }))
      .sort((a, b) => a.score - b.score);
  }, [analytics]);

  const positiveRate = useMemo(() => {
    if (!analytics?.sentimentDistribution) return 0;
    const dist = analytics.sentimentDistribution.map((e) => ({ score: Number(e.score || 0), count: Number(e.count || 0) }));
    const total = dist.reduce((s, d) => s + d.count, 0);
    const positive = dist.filter((d) => d.score >= 4).reduce((s, d) => s + d.count, 0);
    return total ? Math.round((positive / total) * 100) : 0;
  }, [analytics]);

  return (
    <div className="space-y-6">
      {analytics && (
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard title="Category breakdown">
            {categoryData.length ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                No category data yet.
              </div>
            )}
          </ChartCard>
          <ChartCard title="Rating Breakdown (1..5)">
            {ratingData.length ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={ratingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="score" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "rgba(16,185,129,0.08)" }} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                No rating data yet.
              </div>
            )}
          </ChartCard>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">View Ratings</h2>
          <p className="text-sm text-gray-600">Review customer ratings and feedbacks for prioritisation.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={searchVal}
            onChange={(e) => {
              setSearchVal(e.target.value);
            }}
            placeholder="Search name, email, message..."
            className="h-9 w-64 rounded-md border border-gray-200 px-3 text-xs focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            <col className="w-42" />
            <col />
            <col className="w-36" />
            <col className="w-48" />
          </colgroup>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Submitted</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Category</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Rating</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {feedbackItems.map((item) => (
              <tr key={item.feedbackId} className="hover:bg-gray-50">
                <td className="px-3 py-3 text-sm text-gray-600">
                  {formatDate(item.createdAt)}
                  <div className="text-xs truncate max-w-[220px]">
                    {item.submittedEmail ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 mb-1"
                        onClick={() => setPersonModal(item)}
                      >
                        {item.submittedEmail}
                      </button>
                    ) : (
                      <span className="text-gray-400">Anonymous</span>
                    )}
                  </div>
                </td>

                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                  <div className="text-xs text-gray-600 uppercase">{categoryLabels[item.category] || item.category}</div>
                  {item.message && (
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                      onClick={() => setContentModal({ title: 'User Feedback', content: item.message })}
                    >
                      Show feedback
                    </button>
                  )}
                </td>
                <td className="px-3 py-3 text-sm">
                  <div className="flex items-center gap-0.5">
                    {item.rating ? (
                      [...Array(5)].map((_, idx) => (
                        <Star
                          key={idx}
                          className={`w-4 h-4 ${idx < item.rating
                            ? "fill-amber-400 text-amber-400"
                            : "text-gray-200"
                            }`}
                        />
                      ))
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onToggleHighlight(item.feedbackId)}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-all ${item.highlighted
                        ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      {item.highlighted ? "★ Highlighted" : "☆ Highlight"}
                    </button>
                    <button
                      className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-200"
                      onClick={() => setDeleteConfirmModal(item)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!feedbackItems || feedbackItems.length === 0) && !loading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500">
                  No ratings submitted yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="flex items-center justify-center px-4 py-6 text-sm text-gray-500">
            Loading ratings data...
          </div>
        )}
        {!loading && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            <div>Page {pageInfo.page} of {pageInfo.pages} • {pageInfo.total} total</div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => { setPage(1); setLimit(Number(e.target.value) || 20); }}
                className="h-8 rounded-md border border-gray-200 px-2 text-xs"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageInfo.page <= 1}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageInfo.pages || 1, p + 1))}
                disabled={pageInfo.page >= (pageInfo.pages || 1)}
                className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Person details modal */}
      {personModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPersonModal(null)} />
          <div className="relative z-10 w-[90%] max-w-md rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Submitter details</h3>
              <button className="text-gray-500" onClick={() => setPersonModal(null)}>✕</button>
            </div>
            <div className="space-y-2 text-sm text-gray-700">
              <div><span className="font-semibold">Name:</span> {personModal.submittedName || '-'}</div>
              <div><span className="font-semibold">Email:</span> {personModal.submittedEmail || '-'}</div>
              <div><span className="font-semibold">Role:</span> {personModal.role || '-'}</div>
              <div><span className="font-semibold">Organization:</span> {personModal.company || '-'}</div>
              <div><span className="font-semibold">Category:</span> {personModal.category}</div>
              <div><span className="font-semibold">Rating:</span> {personModal.rating ? `${personModal.rating} / 5` : '-'}</div>
              {personModal.acknowledgedAt && (
                <div><span className="font-semibold">Acknowledged At:</span> {formatDate(personModal.acknowledgedAt)}</div>
              )}
              {personModal.responseComment && (
                <div className="mt-2 p-3 bg-slate-50 border-l-4 border-emerald-500 rounded-lg text-xs">
                  <span className="font-semibold block text-slate-800 mb-1">Response Sent:</span>
                  <span className="text-slate-600 italic whitespace-pre-wrap">"{personModal.responseComment}"</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Content modal */}
      {contentModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setContentModal(null)} />
          <div className="relative z-10 w-[92%] max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{contentModal.title || 'Details'}</h3>
              <button className="text-gray-500" onClick={() => setContentModal(null)}>✕</button>
            </div>
            <div className="max-h-[60vh] overflow-auto whitespace-pre-line text-sm text-gray-700">
              {contentModal.content || '-'}
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* Delete confirmation modal */}
      {deleteConfirmModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirmModal(null)} />
          <div className="relative z-10 w-[90%] max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600 mb-3">
                <Trash2 className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Delete Rating</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this rating? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmModal(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const item = deleteConfirmModal;
                  setDeleteConfirmModal(null);
                  try {
                    const { deleteFeedback } = await import('../../apis/supportService');
                    await deleteFeedback(item.feedbackId);
                    toast.success('Rating deleted');
                    await loadFeedback();
                  } catch (e) {
                    toast.error(e?.response?.data?.error || 'Failed to delete');
                  }
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


const ChartCard = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <div className="mt-3 h-40">{children}</div>
  </div>
);

export default SupportFeedbackAdmin;
